// src/pages/collection/CollectionManagement.js
// Página para gestionar catálogos/colecciones con CRUD completo

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button, Form, Spinner, Table, Modal } from 'react-bootstrap';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { useAuth } from '../../context/AuthContext';
import { 
  getCollections, 
  createCollection, 
  updateCollection, 
  deleteCollection,
  getCollectionById 
} from '../../services/collectionApi';
import { storage } from '../../firebase/firebaseConfig';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { Helmet } from 'react-helmet';
import Titulo from '../../elements/Titulo';
import IconUpdate from '../../elements/IconUpdate';
import IconDelete from '../../elements/IconDelete';

const CollectionManagement = () => {
  const { user } = useAuth();
  
  // Estados para el listado
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  
  // Estados para el modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  
  // Estados para el formulario
  const [formData, setFormData] = useState({
    title: '',
    hero: {
      title: '',
      subtitle: '',
      image: '',
      cta: ''
    },
    lifestyle: {
      image: '',
      quote: '',
      author: ''
    }
  });
  
  // Estados para carga de imágenes
  const [heroImage, setHeroImage] = useState(null);
  const [lifestyleImage, setLifestyleImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [heroImagePreview, setHeroImagePreview] = useState('');
  const [lifestyleImagePreview, setLifestyleImagePreview] = useState('');

  // Cargar colecciones
  const loadCollections = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg('');
      
      const { ok, status, data } = await getCollections();
      
      if (ok && (status === 200 || data?.code === 200)) {
        const responseData = data.data || data.message || [];
        setCollections(Array.isArray(responseData) ? responseData : []);
      } else {
        const msg = data?.message || 'Error cargando colecciones';
        setErrorMsg(`${msg} (status: ${status})`);
      }
    } catch (error) {
      console.error('Error cargando colecciones:', error);
      setErrorMsg('Error inesperado al cargar colecciones');
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar colecciones al montar el componente
  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('hero.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        hero: {
          ...prev.hero,
          [field]: value
        }
      }));
    } else if (name.startsWith('lifestyle.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        lifestyle: {
          ...prev.lifestyle,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Subir imagen a Firebase
  const uploadImageToFirebase = async (imageFile, path) => {
    return new Promise((resolve, reject) => {
      const metadata = {
        contentType: imageFile.type || 'image/jpeg'
      };

      const newFileName = `image_${Date.now()}.${imageFile.name.split('.').pop()}`;
      const storageRef = ref(storage, `${path}/${newFileName}`);
      const uploadTask = uploadBytesResumable(storageRef, imageFile, metadata);

      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log('Upload is ' + progress + '% done');
        },
        (error) => {
          console.error('Error al subir imagen:', error);
          reject(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            resolve(downloadURL);
          }).catch(reject);
        }
      );
    });
  };

  // Manejar selección de imagen hero
  const handleHeroImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setHeroImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Manejar selección de imagen lifestyle
  const handleLifestyleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setLifestyleImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLifestyleImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Abrir modal de creación
  const handleOpenCreateModal = () => {
    setFormData({
      title: '',
      hero: {
        title: '',
        subtitle: '',
        image: '',
        cta: ''
      },
      lifestyle: {
        image: '',
        quote: '',
        author: ''
      }
    });
    setHeroImage(null);
    setLifestyleImage(null);
    setHeroImagePreview('');
    setLifestyleImagePreview('');
    setShowCreateModal(true);
  };

  // Cerrar modal de creación
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setFormData({
      title: '',
      hero: {
        title: '',
        subtitle: '',
        image: '',
        cta: ''
      },
      lifestyle: {
        image: '',
        quote: '',
        author: ''
      }
    });
    setHeroImage(null);
    setLifestyleImage(null);
    setHeroImagePreview('');
    setLifestyleImagePreview('');
  };

  // Abrir modal de edición
  const handleOpenEditModal = async (collection) => {
    try {
      setModalLoading(true);
      const { ok, data } = await getCollectionById(collection.id);
      
      if (ok && data?.data) {
        const collectionData = data.data;
        setFormData({
          title: collectionData.title || '',
          hero: {
            title: collectionData.hero?.title || '',
            subtitle: collectionData.hero?.subtitle || '',
            image: collectionData.hero?.image || '',
            cta: collectionData.hero?.cta || ''
          },
          lifestyle: {
            image: collectionData.lifestyle?.image || '',
            quote: collectionData.lifestyle?.quote || '',
            author: collectionData.lifestyle?.author || ''
          }
        });
        setHeroImagePreview(collectionData.hero?.image || '');
        setLifestyleImagePreview(collectionData.lifestyle?.image || '');
        setSelectedCollection(collection);
        setShowEditModal(true);
      }
    } catch (error) {
      setErrorMsg('Error al cargar datos de la colección');
    } finally {
      setModalLoading(false);
    }
  };

  // Cerrar modal de edición
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedCollection(null);
    setFormData({
      title: '',
      hero: {
        title: '',
        subtitle: '',
        image: '',
        cta: ''
      },
      lifestyle: {
        image: '',
        quote: '',
        author: ''
      }
    });
    setHeroImage(null);
    setLifestyleImage(null);
    setHeroImagePreview('');
    setLifestyleImagePreview('');
  };

  // Abrir modal de eliminación
  const handleOpenDeleteModal = (collection) => {
    setSelectedCollection(collection);
    setShowDeleteModal(true);
  };

  // Cerrar modal de eliminación
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedCollection(null);
  };

  // Manejar creación de colección
  const handleCreateCollection = async (e) => {
    e.preventDefault();
    try {
      setModalLoading(true);
      setUploadingImage(true);
      
      let heroImageUrl = formData.hero.image;
      let lifestyleImageUrl = formData.lifestyle.image;
      
      // Subir imágenes a Firebase si hay nuevas
      if (heroImage) {
        heroImageUrl = await uploadImageToFirebase(heroImage, 'collections/hero');
      }
      
      if (lifestyleImage) {
        lifestyleImageUrl = await uploadImageToFirebase(lifestyleImage, 'collections/lifestyle');
      }
      
      const collectionData = {
        title: formData.title,
        hero: {
          ...formData.hero,
          image: heroImageUrl
        },
        products: [],
        lifestyle: {
          ...formData.lifestyle,
          image: lifestyleImageUrl
        }
      };
      
      const { ok, status, data } = await createCollection(collectionData);
      
      if (ok && (status === 201 || data?.code === 201)) {
        setSuccessMsg(data?.message || 'Colección creada exitosamente');
        setShowCreateModal(false);
        loadCollections();
      } else {
        const msg = data?.message || 'Error creando colección';
        setErrorMsg(`${msg} (status: ${status})`);
      }
    } catch (error) {
      console.error('Error creando colección:', error);
      setErrorMsg('Error inesperado al crear colección');
    } finally {
      setModalLoading(false);
      setUploadingImage(false);
    }
  };

  // Manejar actualización de colección
  const handleUpdateCollection = async (e) => {
    e.preventDefault();
    try {
      setModalLoading(true);
      setUploadingImage(true);
      
      let heroImageUrl = formData.hero.image;
      let lifestyleImageUrl = formData.lifestyle.image;
      
      // Subir imágenes a Firebase si hay nuevas
      if (heroImage) {
        heroImageUrl = await uploadImageToFirebase(heroImage, 'collections/hero');
      }
      
      if (lifestyleImage) {
        lifestyleImageUrl = await uploadImageToFirebase(lifestyleImage, 'collections/lifestyle');
      }
      
      const collectionData = {
        title: formData.title,
        hero: {
          ...formData.hero,
          image: heroImageUrl
        },
        lifestyle: {
          ...formData.lifestyle,
          image: lifestyleImageUrl
        }
      };
      
      const { ok, status, data } = await updateCollection(selectedCollection.id, collectionData);
      
      if (ok && (status === 200 || data?.code === 200)) {
        setSuccessMsg(data?.message || 'Colección actualizada exitosamente');
        setShowEditModal(false);
        setSelectedCollection(null);
        loadCollections();
      } else {
        const msg = data?.message || 'Error actualizando colección';
        setErrorMsg(`${msg} (status: ${status})`);
      }
    } catch (error) {
      console.error('Error actualizando colección:', error);
      setErrorMsg('Error inesperado al actualizar colección');
    } finally {
      setModalLoading(false);
      setUploadingImage(false);
    }
  };

  // Manejar eliminación de colección
  const handleDeleteCollection = async () => {
    try {
      setModalLoading(true);
      
      const { ok, status, data } = await deleteCollection(selectedCollection.id);
      
      if (ok && (status === 200 || data?.code === 200)) {
        setSuccessMsg(data?.message || 'Colección eliminada exitosamente');
        setShowDeleteModal(false);
        setSelectedCollection(null);
        loadCollections();
      } else {
        const msg = data?.message || 'Error eliminando colección';
        setErrorMsg(`${msg} (status: ${status})`);
      }
    } catch (error) {
      console.error('Error eliminando colección:', error);
      setErrorMsg('Error inesperado al eliminar colección');
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Gestión de Catálogos - DotoniWoof</title>
      </Helmet>
      <NavContainer />
      <OffCanvasNav />

      <Container fluid className="mt-3">
        <Row>
          <Col>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Gestión de Catálogos</h4>
                <Button variant="primary" onClick={handleOpenCreateModal}>
                  <i className="fas fa-plus me-2"></i>
                  Nuevo Catálogo
                </Button>
              </Card.Header>
              <Card.Body>
                {errorMsg && (
                  <Alert variant="danger" onClose={() => setErrorMsg('')} dismissible>
                    {errorMsg}
                  </Alert>
                )}
                {successMsg && (
                  <Alert variant="success" onClose={() => setSuccessMsg('')} dismissible>
                    {successMsg}
                  </Alert>
                )}

                {/* Tabla de colecciones */}
                {loading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Título</th>
                          <th>Título Hero</th>
                          <th>Subtítulo Hero</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {collections.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center py-4">
                              No hay catálogos registrados
                            </td>
                          </tr>
                        ) : (
                          collections.map((collection) => (
                            <tr key={collection.id}>
                              <td>{collection.id}</td>
                              <td>{collection.title || 'N/A'}</td>
                              <td>{collection.hero?.title || 'N/A'}</td>
                              <td>{collection.hero?.subtitle || 'N/A'}</td>
                              <td>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm" 
                                  className="me-2"
                                  onClick={() => handleOpenEditModal(collection)}
                                >
                                  <IconUpdate />
                                  Editar
                                </Button>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  onClick={() => handleOpenDeleteModal(collection)}
                                >
                                  <IconDelete />
                                  Eliminar
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal de creación de colección */}
        <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Crear Nuevo Catálogo</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleCreateCollection}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Título de la Colección</strong></Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Ej: COLECCIÓN OTOÑO / INVIERNO"
                  required
                />
              </Form.Group>

              <hr />
              <h5>Sección Hero</h5>
              
              <Form.Group className="mb-3">
                <Form.Label><strong>Título Hero</strong></Form.Label>
                <Form.Control
                  type="text"
                  name="hero.title"
                  value={formData.hero.title}
                  onChange={handleInputChange}
                  placeholder="Título principal"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Subtítulo Hero</strong></Form.Label>
                <Form.Control
                  type="text"
                  name="hero.subtitle"
                  value={formData.hero.subtitle}
                  onChange={handleInputChange}
                  placeholder="Subtítulo descriptivo"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Texto CTA (Call to Action)</strong></Form.Label>
                <Form.Control
                  type="text"
                  name="hero.cta"
                  value={formData.hero.cta}
                  onChange={handleInputChange}
                  placeholder="Ej: VER NOVEDADES"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Imagen Hero</strong></Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleHeroImageChange}
                />
                {heroImagePreview && (
                  <div className="mt-2">
                    <img src={heroImagePreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px' }} />
                  </div>
                )}
              </Form.Group>

              <hr />
              <h5>Sección Lifestyle</h5>

              <Form.Group className="mb-3">
                <Form.Label><strong>Imagen Lifestyle</strong></Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleLifestyleImageChange}
                />
                {lifestyleImagePreview && (
                  <div className="mt-2">
                    <img src={lifestyleImagePreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px' }} />
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Cita (Quote)</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="lifestyle.quote"
                  value={formData.lifestyle.quote}
                  onChange={handleInputChange}
                  placeholder="Cita o frase inspiradora"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Autor</strong></Form.Label>
                <Form.Control
                  type="text"
                  name="lifestyle.author"
                  value={formData.lifestyle.author}
                  onChange={handleInputChange}
                  placeholder="Ej: Editorial Pets 2024"
                />
              </Form.Group>

              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={handleCloseCreateModal}>
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={modalLoading || uploadingImage}>
                  {modalLoading || uploadingImage ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {uploadingImage ? 'Subiendo imágenes...' : 'Creando...'}
                    </>
                  ) : (
                    'Crear Catálogo'
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Modal de edición de colección */}
        <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Editar Catálogo</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalLoading && !selectedCollection ? (
              <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" />
              </div>
            ) : (
              <Form onSubmit={handleUpdateCollection}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Título de la Colección</strong></Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>

                <hr />
                <h5>Sección Hero</h5>
                
                <Form.Group className="mb-3">
                  <Form.Label><strong>Título Hero</strong></Form.Label>
                  <Form.Control
                    type="text"
                    name="hero.title"
                    value={formData.hero.title}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Subtítulo Hero</strong></Form.Label>
                  <Form.Control
                    type="text"
                    name="hero.subtitle"
                    value={formData.hero.subtitle}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Texto CTA (Call to Action)</strong></Form.Label>
                  <Form.Control
                    type="text"
                    name="hero.cta"
                    value={formData.hero.cta}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Imagen Hero</strong></Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleHeroImageChange}
                  />
                  {heroImagePreview && (
                    <div className="mt-2">
                      <img src={heroImagePreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px' }} />
                    </div>
                  )}
                </Form.Group>

                <hr />
                <h5>Sección Lifestyle</h5>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Imagen Lifestyle</strong></Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleLifestyleImageChange}
                  />
                  {lifestyleImagePreview && (
                    <div className="mt-2">
                      <img src={lifestyleImagePreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px' }} />
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Cita (Quote)</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="lifestyle.quote"
                    value={formData.lifestyle.quote}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Autor</strong></Form.Label>
                  <Form.Control
                    type="text"
                    name="lifestyle.author"
                    value={formData.lifestyle.author}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <div className="d-flex justify-content-end gap-2">
                  <Button variant="secondary" onClick={handleCloseEditModal}>
                    Cancelar
                  </Button>
                  <Button variant="primary" type="submit" disabled={modalLoading || uploadingImage}>
                    {modalLoading || uploadingImage ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {uploadingImage ? 'Subiendo imágenes...' : 'Actualizando...'}
                      </>
                    ) : (
                      'Actualizar Catálogo'
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Modal.Body>
        </Modal>

        {/* Modal de confirmación de eliminación */}
        <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
          <Modal.Header closeButton>
            <Modal.Title>Confirmar Eliminación</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            ¿Estás seguro de que deseas eliminar el catálogo "{selectedCollection?.title}"? 
            Esta acción no se puede deshacer.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDeleteModal}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDeleteCollection} disabled={modalLoading}>
              {modalLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default CollectionManagement;
